// Owner: tenant-connect (owner feedback F18). Settings › Connections › Microsoft & Google: the organisation's
// Microsoft 365 or Google Workspace account. Other apps (Attio, HTTP / REST, GitHub) have their own page,
// Settings › Connections › Apps: the former `?view=apps` addresses land there.
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { AppsSearch } from '../../../features/integrations/apps/search';
import { type ConnectionsSearch, ConnectionsPage } from '../../../features/tenant-connect/ConnectionsPage';

const Search = z.object({
  // Former tabs: `people`, `mail` and `signin` moved to Settings › People › Directory, Settings › E-mail and
  // Settings › Sign-in (redirected below), `guide` falls back to the overview.
  tab: z.enum(['overview', 'people', 'mail', 'signin', 'activity', 'simulation']).optional().catch(undefined),
  tenant: z.enum(['ok', 'error']).optional().catch(undefined),
  message: z.string().max(400).optional().catch(undefined),
  connection: z.string().max(80).optional().catch(undefined),
  // Former Apps view (`?view=apps&app=…&atab=…&github=…`): redirected to /settings/apps.
  view: z.enum(['accounts', 'apps']).optional().catch(undefined),
  ...AppsSearch.shape,
});

export const Route = createFileRoute('/_settings/settings/connections')({
  validateSearch: Search,
  beforeLoad: ({ search }) => {
    if (search.view === 'apps')
      throw redirect({
        to: '/settings/apps',
        search: { app: search.app, atab: search.atab, github: search.github },
        replace: true,
      });
    if (search.tab === 'people') throw redirect({ to: '/settings/directory', replace: true });
    if (search.tab === 'mail') throw redirect({ to: '/settings/email', replace: true });
    if (search.tab === 'signin') throw redirect({ to: '/settings/sign-in', replace: true });
  },
  component: ConnectionsRoute,
});

function ConnectionsRoute() {
  const { view: _view, app: _app, atab: _atab, github: _github, tab, ...rest } = Route.useSearch();
  const search: ConnectionsSearch = {
    ...rest,
    ...(tab && tab !== 'people' && tab !== 'mail' && tab !== 'signin' ? { tab } : {}),
  };
  const navigate = Route.useNavigate();
  return (
    <ConnectionsPage search={search} onSearch={(next) => void navigate({ search: next, replace: true })} />
  );
}
